import type { HermesAPI } from "./api";
import { arr, bool, int, num, obj, s, str } from "./json";

// 分享 Bot（connector 自己的接口 /timibot/v1/*，经同一条加密隧道；见 docs/RELAY.md“分享 Bot”）

/** 一个 Bot 本月的 token 用量和上限（输入 + 输出，不含缓存命中；含主人自己的用量） */
export interface BotUsage {
  profile: string;
  used_tokens?: number;
  /** 不限时为空 */
  monthly_tokens?: number;
  period_start?: string;
}

function parseUsage(o: Record<string, unknown> | undefined): BotUsage {
  return { profile: s(o?.profile) ?? "", used_tokens: int(o?.used_tokens), monthly_tokens: int(o?.monthly_tokens), period_start: s(o?.period_start) };
}

export function usageExhausted(u: BotUsage): boolean {
  return u.used_tokens !== undefined && u.monthly_tokens !== undefined && u.used_tokens >= u.monthly_tokens;
}

export interface ShareInvite {
  id: string;
  profile: string;
  /** 短邀请码（8 位）；中转登记失败时为空，只能用链接 */
  short?: string;
  link: string;
  created?: number;
  expires?: number;
}

export interface ShareGuest { id: string; name?: string; added?: number; last_seen?: number; online: boolean }

export interface BotShares {
  profile: string;
  invites: ShareInvite[];
  guests: ShareGuest[];
  usage: BotUsage;
  /** "main"：就是主账号；"follow"：API Key 跟随主账号；"own"：自己的一份；空：connector 太旧 */
  credentials?: string;
  /** 生图 / 生视频设置，取值同上：跟随主账号的 Bot 由 connector 同步（connector 1.10）；空：connector 太旧 */
  media?: string;
}

function parseInvite(raw: unknown): ShareInvite | undefined {
  const o = obj(raw);
  const id = s(o?.id);
  return o && id ? { id, profile: s(o.profile) ?? "", short: s(o.short), link: s(o.link) ?? "", created: num(o.created), expires: num(o.expires) } : undefined;
}

/** 这台设备在主机上的身份；访客还会带上可用 Bot 的额度 */
export async function shareMe(api: HermesAPI): Promise<{ role: string; profiles?: string[]; usage: BotUsage[]; voice?: { key: string; region?: string } }> {
  const o = obj(await api.client.broker.getJSON("/timibot/v1/me"));
  const voice = obj(o?.voice);
  return {
    role: s(o?.role) ?? "owner",
    profiles: (arr(o?.profiles) ?? []).filter((x): x is string => typeof x === "string"),
    usage: (arr(o?.usage) ?? []).map((u) => parseUsage(obj(u))),
    voice: voice && s(voice.key) ? { key: s(voice.key)!, region: s(voice.region) } : undefined,
  };
}

/** 某个 Bot 的分享情况（只有主人能看）。profile 用 profile 名，默认 Bot 是 "default" */
export async function botShares(api: HermesAPI, profile: string): Promise<BotShares> {
  const o = obj(await api.client.broker.getJSON("/timibot/v1/shares", { profile }));
  return {
    profile: s(o?.profile) ?? profile,
    invites: (arr(o?.invites) ?? []).map(parseInvite).filter((x): x is ShareInvite => !!x),
    guests: (arr(o?.guests) ?? []).map(obj).filter(Boolean).flatMap((g) => {
      const id = s(g!.id);
      return id ? [{ id, name: s(g!.name), added: num(g!.added), last_seen: num(g!.last_seen), online: bool(g!.online) ?? false }] : [];
    }),
    usage: parseUsage(o),
    credentials: s(o?.credentials),
    media: s(o?.media),
  };
}

/** 生成邀请：有效期内谁拿到都能用这个 Bot */
export async function createInvite(api: HermesAPI, profile: string, title: string | undefined, days = 7): Promise<ShareInvite> {
  const inv = parseInvite(await api.client.broker.sendJSON("POST", "/timibot/v1/invites", { profile, title: title ?? null, days }));
  if (!inv) throw new Error("connector 没有返回邀请");
  return inv;
}

export async function revokeInvite(api: HermesAPI, id: string): Promise<void> {
  await api.client.broker.sendJSON("DELETE", `/timibot/v1/invites/${encodeURIComponent(id)}`, {});
}

/** 取消某位访客对这个 Bot 的使用权（它正在用的连接会被断开） */
export async function removeGuest(api: HermesAPI, id: string, profile: string): Promise<void> {
  await api.client.broker.sendJSON("DELETE", `/timibot/v1/guests/${encodeURIComponent(id)}`, {}, { profile });
}

/** 让这个 Bot 的 API Key 跟随主账号。返回新的状态 */
export async function followMainCredentials(api: HermesAPI, profile: string): Promise<string | undefined> {
  return str(obj(await api.client.broker.sendJSON("POST", `/timibot/v1/bots/${encodeURIComponent(profile)}/follow`, {}))?.credentials);
}

/** 设置每月 token 上限；空 = 不限 */
export async function setMonthlyLimit(api: HermesAPI, profile: string, tokens: number | undefined): Promise<BotUsage> {
  return parseUsage(obj(await api.client.broker.sendJSON("PUT", `/timibot/v1/limits/${encodeURIComponent(profile)}`, { monthly_tokens: tokens ?? null })));
}

export async function insights(api: HermesAPI, days: number, profile?: string): Promise<{ sessions: number; messages: number }> {
  const o = obj(await api.client.call("insights.get", profile ? { days, profile } : { days }));
  return { sessions: int(o?.sessions) ?? 0, messages: int(o?.messages) ?? 0 };
}

export const TokenCount = {
  /** 12,345 → “1.2 万”，3,000,000 → “300 万” */
  short(n: number): string {
    if (n < 10000) return n.toLocaleString("zh-CN");
    const wan = n / 10000;
    return (wan >= 100 || wan === Math.round(wan) ? wan.toFixed(0) : wan.toFixed(1)) + " 万";
  },
  usage(u: BotUsage): string {
    return `${u.used_tokens !== undefined ? this.short(u.used_tokens) : "—"} / ${u.monthly_tokens !== undefined ? this.short(u.monthly_tokens) : "不限"}`;
  },
};
